import { useCallback, useEffect, useState } from "react";
import { statusApi } from "../api/status";
import type {
  GraphSource,
  KpiData,
  NarrativeData,
  Platform,
  RangePreset,
  StatusSummary,
  UptimeGraphData,
} from "../types/status";

export function useOverviewData(platform?: Platform) {
  const [summary, setSummary] = useState<StatusSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [range, setRange] = useState<RangePreset>("7");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const [platformKpis, setPlatformKpis] = useState<KpiData | null>(null);
  const [frontendKpis, setFrontendKpis] = useState<KpiData | null>(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [narrative, setNarrative] = useState<NarrativeData | null>(null);
  const [narrativeLoading, setNarrativeLoading] = useState(true);
  const [graphSource, setGraphSource] = useState<GraphSource>("all");
  const [uptimeGraph, setUptimeGraph] = useState<UptimeGraphData | null>(null);
  const [uptimeLoading, setUptimeLoading] = useState(true);

  const reload = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await statusApi.getSummary({ platform });
      setSummary(res.data);
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [platform]);

  const rangeParams = useCallback(() => (
    range === "custom" && customFrom && customTo
      ? { from_dt: customFrom, to_dt: customTo, platform }
      : { days: parseInt(range), platform }
  ), [range, customFrom, customTo, platform]);

  const loadKpis = useCallback(async () => {
    setKpiLoading(true);
    try {
      const base = rangeParams();
      const [platformRes, frontendRes] = await Promise.all([
        statusApi.getKpis({ ...base, source: "platform" }),
        statusApi.getKpis({ ...base, source: "frontend" }),
      ]);
      setPlatformKpis(platformRes.data);
      setFrontendKpis(frontendRes.data);
    } catch {
      // silently ignore
    } finally {
      setKpiLoading(false);
    }
  }, [rangeParams]);

  const loadNarrative = useCallback(async () => {
    setNarrativeLoading(true);
    try {
      const res = await statusApi.getNarrative(rangeParams());
      setNarrative(res.data);
    } catch {
      setNarrative(null);
    } finally {
      setNarrativeLoading(false);
    }
  }, [rangeParams]);

  const loadUptimeGraph = useCallback(async () => {
    setUptimeLoading(true);
    try {
      const base = rangeParams();
      const res = await statusApi.getUptimeGraph(
        graphSource === "all" ? base : { ...base, source: graphSource },
      );
      setUptimeGraph(res.data);
    } catch {
      setUptimeGraph(null);
    } finally {
      setUptimeLoading(false);
    }
  }, [rangeParams, graphSource]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { loadKpis(); }, [loadKpis]);
  useEffect(() => { loadNarrative(); }, [loadNarrative]);
  useEffect(() => { loadUptimeGraph(); }, [loadUptimeGraph]);

  return {
    summary, loading, refreshing, reload,
    range, setRange, customFrom, setCustomFrom, customTo, setCustomTo,
    platformKpis, frontendKpis, kpiLoading,
    narrative, narrativeLoading,
    graphSource, setGraphSource,
    uptimeGraph, uptimeLoading,
  };
}
